// src/app/services/projectApi.ts
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './apiConfig';
import {
  ICriticalTask,
  IIdName,
  IProject,
  IProjectAhu,
  IProjectQueryParams,
  ITimelineItem,
  IWorkflowSetChildData,
} from '../models';
export const projectApi = createApi({
  reducerPath: 'projectApi',
  baseQuery,
  endpoints: (builder) => ({
    getProjectById: builder.query<IProject, number>({
      query: (id) => `/api/v1.0/projects/${id}`,
    }),
    getProjects: builder.query<IProject[], IProjectQueryParams>({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.pin !== undefined) {
          queryParams.append('pinnned', String(params.pin));
        }
        if (params.name) {
          queryParams.append('name', params.name);
        }
        if (params.page) {
          queryParams.append('page', String(params.page));
        }
        // Add any other parameters
        Object.keys(params).forEach((key) => {
          if (
            key !== 'pin' &&
            key !== 'name' &&
            key !== 'page' &&
            params[key] !== undefined &&
            params[key] !== null
          ) {
            queryParams.append(key, String(params[key]));
          }
        });
        const queryString = queryParams.toString();
        return `/api/v1.0/projects/${queryString ? `?${queryString}` : ''}`;
      },
    }),
    editProject: builder.mutation<
      IProject,
      { projectId: number; updatedProject: Partial<IProject> }
    >({
      query: ({ projectId, updatedProject }) => ({
        url: `/${projectId}`,
        method: 'PUT',
        body: updatedProject,
      }),
    }),
    deleteProject: builder.mutation<void, string>({
      query: (projectId) => ({
        url: `/${projectId}`,
        method: 'DELETE',
      }),
    }),
    getTimeline: builder.query<ITimelineItem[], number>({
      query: (id) => `/api/v1.0/projects/${id}/timeline/`,
    }),
    getTasks: builder.query<IIdName[], void>({
      query: () => `api/v1.0/cms/1/v1.0.0/workflow/tasks/`,
      keepUnusedDataFor: Infinity, // Cache data indefinitely
    }),
    getTaskStatuses: builder.query<IIdName[], void>({
      query: () => `/api/v1.0/master/v1.0.0/ahu/task_statuses/`,
      keepUnusedDataFor: Infinity, // Cache data indefinitely
    }),
    getCriticalTasks: builder.query<ICriticalTask[], void>({
      query: () => `api/v1.0/cms/1/v1.0.0/workflow/critical_tasks/`,
      keepUnusedDataFor: Infinity, // Cache data indefinitely
    }),
    getProjectAhus: builder.query<IProjectAhu[], number>({
      query: (id) => `/api/v1.0/ahu/units/?ahu_list=true&project_id=${id}`,
    }),
    setTimelineTaskStatus: builder.mutation<
      ITimelineItem,
      IWorkflowSetChildData
    >({
      query: (queryItem) => ({
        url: `/api/v1.0/projects/${queryItem.project_id}/timeline/${queryItem.id}`,
        method: 'PATCH',
        body: JSON.stringify(queryItem),
      }),
    }),
  }),
});
export const {
  useGetProjectsQuery,
  useLazyGetProjectsQuery,
  useEditProjectMutation,
  useDeleteProjectMutation,
  useLazyGetProjectByIdQuery,
  useLazyGetTimelineQuery,
  useGetTaskStatusesQuery,
  useLazyGetTaskStatusesQuery,
  useGetTasksQuery,
  useLazyGetTasksQuery,
  useGetCriticalTasksQuery,
  useLazyGetCriticalTasksQuery,
  useLazyGetProjectAhusQuery,
  useSetTimelineTaskStatusMutation,
} = projectApi;
